import { motion } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";

export default function MeetDash() {
  return (
    <section data-testid="meet-dash-section" className="relative py-24 md:py-36 overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 70% 60%, rgba(0,204,204,0.14), transparent 60%), radial-gradient(ellipse at 20% 30%, rgba(0,204,204,0.06), transparent 60%), #0D0F12",
        }}
      />
      <div className="absolute inset-0 grid-bg opacity-15" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-4 lg:gap-2 items-center">
        {/* Text column (left) */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-6"
        >
          <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Meet Dash</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Meet DASH — <br />
            <span className="text-gradient-cyan">the fearless spirit behind Biker Boulevard.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-lg max-w-xl leading-relaxed">
            A symbol of freedom, ambition, curiosity, and the thrill of the open road. He&apos;s always ready for one more ride, one more adventure, and one more story worth telling. Because for DASH, fast isn&apos;t just how he rides — it&apos;s how he chases his dreams.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {[
              { v: "01", l: "Squad" },
              { v: "∞", l: "Miles" },
              { v: "24/7", l: "Boulevard" },
            ].map((s) => (
              <div key={s.l} className="p-4 rounded-xl glass" data-testid={`dash-stat-${s.l.toLowerCase()}`}>
                <div className="text-xl font-mono font-bold text-white">{s.v}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-mono">{s.l}</div>
              </div>
            ))}
          </div>

          <a
            href="#revvpass"
            data-testid="dash-cta"
            className="mt-9 group inline-flex items-center gap-2 px-8 py-4 rounded-full border border-[#00cccc]/40 text-[#00cccc] hover:bg-[#00cccc] hover:text-black hover:shadow-[0_0_28px_rgba(0,204,204,0.55)] transition-all duration-300"
          >
            Join the Squad
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </motion.div>

        {/* Mascot column (right) — scroll parallax + idle float + wave */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="lg:col-span-6 relative flex justify-center lg:justify-start lg:-ml-6"
        >
          <div className="relative">
            {/* Cyan glow behind mascot */}
            <div
              className="absolute inset-0 blur-3xl -z-10 scale-90"
              style={{ background: "radial-gradient(circle at center, rgba(0,204,204,0.35), transparent 65%)" }}
            />

            {/* Rotating cyan ring */}
            <motion.div
              className="absolute left-1/2 bottom-8 -translate-x-1/2 -z-10 rounded-full"
              style={{
                width: "70%",
                height: "70%",
                aspectRatio: "1",
                border: "1px dashed rgba(0,204,204,0.35)",
                boxShadow: "0 0 40px rgba(0,204,204,0.2), inset 0 0 40px rgba(0,204,204,0.1)",
              }}
              animate={{ rotate: 360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            />

            {/* Dash — gentle idle float */}
            <motion.img
              src={`${process.env.PUBLIC_URL}/mascot.png`}
              alt="Dash — the Biker Boulevard mascot"
              data-testid="dash-image"
              style={{ filter: "drop-shadow(0 30px 40px rgba(0,204,204,0.35)) drop-shadow(0 10px 20px rgba(0,0,0,0.6))" }}
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-[300px] sm:w-[380px] lg:w-[480px] max-w-full select-none pointer-events-none"
              draggable="false"
            />

            {/* Ground reflection */}
            <div
              className="absolute left-1/2 -translate-x-1/2 bottom-[-30px] rounded-full opacity-70"
              style={{
                width: "60%",
                height: "22px",
                background: "radial-gradient(ellipse at center, rgba(0,204,204,0.55), transparent 70%)",
                filter: "blur(6px)",
              }}
            />

            {/* Floating HUD chips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="hidden md:flex absolute top-[15%] -right-6 lg:-right-10 glass rounded-xl px-3 py-2 items-center gap-2 animate-float-y"
              style={{ animationDelay: "0.2s" }}
            >
              <Zap className="w-3.5 h-3.5 text-[#00cccc]" />
              <div className="leading-tight">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Rider ID</div>
                <div className="text-xs text-white font-mono">DSH-001</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="hidden md:flex absolute bottom-[4%] -left-6 lg:-left-12 glass rounded-xl px-3 py-2 items-center gap-2 animate-float-y"
              style={{ animationDelay: "1.2s" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00cccc] animate-pulse-glow" />
              <div className="leading-tight">
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-mono">Status</div>
                <div className="text-xs text-[#00cccc] font-mono">Boulevard-Ready</div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
